import { useCallback, useMemo } from "react";
import "../css/Footer.css";
import MarkitFullLogoBlack from "../assets/MarkitFullLogoBlack.png";
import MarkitFullLogoWhite from "../assets/MarkitFullLogoWhite.png";
import { WhiteLabelColors, WhiteLabelSocial } from "../macros/WhiteLabelUsers";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../redux/slices/accountSlice";
import { AccountData, ThemeType } from "@markit/common.types";
import { Colors } from "../utils/colors";
import {
  APPSTORE,
  GOOGLEPLAY,
  MARKIT_CONTACT_US,
  MARKIT_DOCS,
  MARKIT_IG,
  MARKIT_LINKEDIN,
  MARKIT_PRIVACY_POLICY,
  MARKIT_TERMS_OF_USE,
  MARKIT_TIKTOK,
  MARKIT_X,
  MARKITAI,
} from "@markit/common.utils";
import { LightTheme, useTheme } from "../hooks/useTheme";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "./Dividers/HorizontalDivider";
import TextHoverButton from "./Buttons/TextHoverButton";
import RectangleButton from "./Buttons/RectangleButton";
import { useWindowResize } from "../hooks/useWindowResize";
import { checkIsDesktop } from "../utils/screenResponsivenessUtils";
import { useNavigate } from "react-router-dom";
import { DataLoaders } from "../redux/slices/dataSlice";
import ProfilePic from "./ProfilePic";
import MarkitSecuredContainer from "./UserInfoContainers/MarkitSecuredContainer";

type FooterProp = {
  showRCA?: boolean;
  eventTheme?: ThemeType;
  forceLight?: boolean;
  creatorData?: AccountData;
};

const Footer = (props: FooterProp) => {
  const { showRCA, eventTheme, forceLight, creatorData } = props;
  const { account } = useSelector(getAccountState);
  const { isRCA } = account;
  const { theme } = useTheme(eventTheme, forceLight);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const styles = {
    iconStyle: {
      fontSize: 25,
      marginTop: 5,
      color: "#929292",
    },
    bodyMedium: {
      fontSize: 14,
      fontWeight: 500,
    },
  };

  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const isLightTheme = useMemo(() => theme === LightTheme, [theme]);

  const bgColor = useMemo(
    () => theme.SubComponentBG.backgroundColor,
    [theme.SubComponentBG.backgroundColor]
  );

  const navigateProfileOnPress = useCallback(() => {
    if (!creatorData) return;
    dispatch(DataLoaders.user(creatorData.uid));
    navigate(`/u/${creatorData.username}`, {
      state: { username: creatorData.username },
    });
  }, [creatorData, dispatch, navigate]);

  const renderOptionText = useCallback(
    (url: string, text: string, sameTab?: boolean) =>
      sameTab ? (
        <div
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          onClick={() => navigate(url)}
        >
          <p className="FooterInfoText">{text}</p>
        </div>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <p className="FooterInfoText">{text}</p>
        </a>
      ),
    [navigate]
  );

  const getStartedButton = useMemo(
    () => (
      <RectangleButton
        buttonLabel="Get Started"
        onPress={() => {
          window.open(MARKITAI + "/welcome");
        }}
        altColor={isLightTheme ? Colors.WHITE1 : Colors.GRAY10}
        altTextColor={isLightTheme ? Colors.BLACK : Colors.GRAY1}
        containerStyles={{
          paddingInline: 16,
          paddingBlock: 12,
          borderRadius: 10,
          border: `0.5px solid ${isLightTheme ? Colors.GRAY2 : Colors.BLACK1}`,
        }}
        iconRight={
          <Icon
            icon="ion:chevron-forward"
            height={14}
            style={{ color: isLightTheme ? Colors.BLACK : Colors.GRAY1 }}
          />
        }
      />
    ),
    [isLightTheme]
  );

  const socialMediaSection = useMemo(
    () => (
      <div className="AlignedRow" style={{ gap: 20 }}>
        <div className="FooterAppIcon">
          <Icon
            icon="mdi:instagram"
            height={20}
            onClick={() => window.open(MARKIT_IG)}
            style={styles.iconStyle}
          />
        </div>
        <div className="FooterAppIcon">
          <Icon
            icon="ri:twitter-x-fill"
            height={20}
            onClick={() => window.open(MARKIT_X)}
            style={styles.iconStyle}
          />
        </div>
        <div className="FooterAppIcon">
          <Icon
            icon="ri:linkedin-fill"
            height={20}
            onClick={() => window.open(MARKIT_LINKEDIN)}
            style={styles.iconStyle}
          />
        </div>
        <div className="FooterAppIcon">
          <Icon
            icon="ic:baseline-tiktok"
            height={20}
            onClick={() => window.open(MARKIT_TIKTOK)}
            style={styles.iconStyle}
          />
        </div>
      </div>
    ),
    [styles.iconStyle]
  );

  // Creator page custom footer
  if (creatorData) {
    return (
      <div
        className="ColumnNormal"
        style={{
          backgroundColor: bgColor,
          padding: isDesktopSize ? "40px 0px" : "40px 0px",
          gap: 40,
        }}
      >
        <HorizontalDivider altMargin={0} eventTheme={eventTheme} altWidth={1} />
        <div className="ColumnNormal" style={{ gap: 20 }}>
          <div className="ColumnCentering" style={{ gap: 20 }}>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <div>
                <ProfilePic
                  user={creatorData}
                  size={31}
                  onPress={navigateProfileOnPress}
                />
              </div>
              <span
                className="smallBodySubtext"
                style={{ width: 186, textAlign: "center" }}
              >
                <span className="bodySmallMedium" style={theme.PrimaryText}>
                  {creatorData.fullName}
                </span>{" "}
                uses Markit to send updates, reminders, and more.
              </span>
            </div>
            <MarkitSecuredContainer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="ColumnNormal"
      style={{
        backgroundColor: bgColor,
        padding: isDesktopSize ? "40px 80px" : "40px 20px",
      }}
    >
      <div
        className={isDesktopSize ? "AlignedRowSpaced" : "ColumnNormal"}
        style={{ gap: isDesktopSize ? 0 : 20 }}
      >
        {!isRCA ? (
          <img
            className="LogoFooter"
            src={isLightTheme ? MarkitFullLogoBlack : MarkitFullLogoWhite}
            alt={"banner"}
            style={{ width: 123, objectFit: "contain", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        ) : null}
        <p
          style={{
            fontSize: isDesktopSize ? 20 : 16,
            fontWeight: 500,
            color: Colors.GRAY1,
          }}
        >
          Texting that feels Human.
        </p>
      </div>
      <HorizontalDivider altMargin={40} eventTheme={eventTheme} altWidth={1} />
      <div
        style={{
          ...(isRCA && showRCA
            ? { backgroundColor: WhiteLabelColors.RCA_TRANSPARENT }
            : { backgroundColor: bgColor }),
        }}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 40, paddingBottom: isDesktopSize ? 60 : 40 }}
        >
          <div
            className={isDesktopSize ? "AlignedRowSpaced" : "AlignedRow"}
            style={{ alignItems: "flex-start", gap: 40 }}
          >
            <div
              className="AlignedRow"
              style={{
                gap: 40,
                alignItems: "flex-start",
                display: isDesktopSize ? "flex" : "grid",
                gridTemplateColumns: isDesktopSize ? "none" : "repeat(2, 1fr)",
              }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <>
                  {isRCA && showRCA ? (
                    <div
                      className="bodyMedium"
                      style={theme.PrimaryText}
                      onClick={() => {
                        window.open(WhiteLabelSocial.RCA_WEBSITE);
                      }}
                    >
                      Red Curtain Addict
                    </div>
                  ) : (
                    <p className="bodyMedium" style={theme.PrimaryText}>
                      Markit AI
                    </p>
                  )}
                </>
                {renderOptionText("/texting", "Texting", true)}
                {renderOptionText("/explore", "Discover", true)}
                {renderOptionText("/pricing", "Pricing", true)}
                {renderOptionText("/welcome", "Sign Up", true)}
              </div>
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <p className="bodyMedium" style={theme.PrimaryText}>
                  Resources
                </p>
                {renderOptionText(MARKIT_DOCS, "Documentation")}
                {renderOptionText(
                  MARKIT_DOCS + "/contactsupport",
                  "Contact Sales"
                )}
                {renderOptionText(
                  MARKIT_DOCS + "/joinourdiscord",
                  "Join our Discord"
                )}
              </div>
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <p className="bodyMedium" style={theme.PrimaryText}>
                  Download
                </p>
                {renderOptionText(APPSTORE, "iOS")}
                {renderOptionText(GOOGLEPLAY, "Android")}
              </div>
              {!isDesktopSize ? socialMediaSection : null}
            </div>
            {isDesktopSize ? (
              <div className="ColumnNormal" style={{ gap: 20 }}>
                {socialMediaSection}
                {getStartedButton}
              </div>
            ) : null}
          </div>
          {!isDesktopSize ? getStartedButton : null}
        </div>
        <HorizontalDivider altMargin={0} eventTheme={eventTheme} altWidth={1} />
        <div
          className={isDesktopSize ? "AlignedRowSpaced" : "ColumnCentering"}
          style={{
            paddingTop: isDesktopSize ? 20 : 40,
            gap: isDesktopSize ? 0 : 20,
          }}
        >
          {isDesktopSize ? (
            <p className="bodySubtextMedium">
              © Markit Social Inc. All Rights Reserved.
            </p>
          ) : null}
          <div className="AlignedRow" style={{ gap: 20 }}>
            <TextHoverButton
              text="Terms"
              onPress={() => {
                window.open(MARKIT_TERMS_OF_USE);
              }}
              textStyles={styles.bodyMedium}
            />
            <TextHoverButton
              text="Privacy"
              onPress={() => {
                window.open(MARKIT_PRIVACY_POLICY);
              }}
              textStyles={styles.bodyMedium}
            />
            <TextHoverButton
              text="Support"
              onPress={() => {
                window.open(MARKIT_CONTACT_US);
              }}
              textStyles={styles.bodyMedium}
            />
          </div>
          {!isDesktopSize ? (
            <p className="bodySubtextMedium">
              © Markit Social Inc. All Rights reserved.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Footer;
